import { useSignal, useComputed } from '@preact/signals-react'

import Button from 'react-bootstrap/Button'
import Container from 'react-bootstrap/Container'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'

import Icon from '../../../components/Icon'

function CreateInstallationModal({ show, handleCancel, handleConfirm, businessEntityId, availableAgentsVersions, availableProfiles, availableUtgVersions, defaultAgentVersion, defaultUtgVersion, installationManifestObject, intent }) {

    const InstallationModeEnum = Object.freeze({
        INIT: 0,
        NEW_INSTALL: 10,
        EXISTING_INSTALL: 30
    })

    const utgInstallLocationEnum = Object.freeze({
        NEW_INSTALL: "c:\\shift4\\",
        EXISTING_INSTALL: "c:\\shift4\\utg2"
    })

    const getInitialInstallationMode = (intent) => {
        switch (intent) {
            case "install":
                return InstallationModeEnum.NEW_INSTALL
            case "connect":
                return InstallationModeEnum.EXISTING_INSTALL
            default:
                return InstallationModeEnum.INIT
        }
    }

    const installationModeSignal = useSignal(getInitialInstallationMode(intent))
    const utgVersionTargetSignal = useSignal(defaultUtgVersion)
    const agentVersionTargetSignal = useSignal(defaultAgentVersion)
    const profileTargetSignal = useSignal('No Profile')
    const installationNameSignal = useSignal('')
    const agentInstallLocationSignal = useSignal('c:\\shift4\\Shift4 Command Center\\agent')
    const utgInstallLocationSignal = useSignal(utgInstallLocationEnum.NEW_INSTALL)
    const formErrorSignal = useSignal(false)
    const formErrorTextSignal = useSignal('')

    const installationObjectComputed = useComputed(
        () => {
            const data = {
                "type": 0,
                "businessEntityId": `${businessEntityId}`,
                "manifest": {
                    "machine": {
                        "agentVersion": null,
                        "permanentId": null,
                        "createKeyContainer": true,
                        "serverName": null,
                        "nickname": `${installationNameSignal.value}`,
                        "dnsName": null,
                        "lastUpdatedAt": null,
                        "lastUpdatedBy": null,
                        "status": null,
                        "ipAddress": null,
                        "operatingSystem": null,
                        "machineStatus": null,
                        "businessEntityId": `${businessEntityId}`,
                        "components": []
                    },
                    "agent": {
                        "installLocation": `${agentInstallLocationSignal.value}`,
                        "downloadLocation": null,
                        "createNewUser": true,
                        "newMachine": true,
                        "registerAgent": true,
                        "registerComponents": true,
                        "loadMasterKey": true
                    },
                    "credentials": {
                        "sessionToken": "",
                        "applicationId": 4,
                        "authenticationCode": "",
                        "businessEntityId": `${businessEntityId}`
                    },
                    "components": [
                        {
                            "connectionString": null,
                            "currentTaskHash": null,
                            "downloadLocation": null,
                            "hasConfigChanges": false,
                            "lastTaskHash": null,
                            "lastUpdatedAt": null,
                            "lastUpdatedBy": null,
                            "localConfigRevisionId": null,
                            "remoteConfigRevisionId": null,
                            "machineId": null,
                            "metadata": null,
                            "operatingSystem": null,
                            "permanentId": null,
                            "profileBusinessEntityId": 0,
                            "serverName": null,
                            "softwareVersion": null,
                            "nickname": null,
                            "type": 1,
                            "runInstall": false,
                            "updates": null,
                            "installLocation": `${utgInstallLocationSignal.value}`,
                            "in4mListenerPort": null,
                            "fileId": 0,
                            "componentSerialSlotAssignmentId": 0,
                            "businessEntityId": `${businessEntityId}`,
                            "profileId": null,
                            "tasks": [],
                            "componentStatus": null
                        }
                    ],
                    "tasks": []
                }
            }

            if (installationModeSignal.value === InstallationModeEnum.NEW_INSTALL) {
                data.type = InstallationModeEnum.NEW_INSTALL
                data.manifest.agent.downloadLocation = getAgentDownloadLocation()
                data.manifest.agent.agentVersion = extractVersion(agentVersionTargetSignal.value)
                data.manifest.components[0].utgVersion = extractVersion(utgVersionTargetSignal.value)
                data.manifest.components[0].downloadLocation = getUtgDownloadLocation()

                if (profileTargetSignal.value != "No Profile") {
                    // Value of Profile options is BeId-ProfileId ex."8647-3678"
                    const match = profileTargetSignal.value.match(/(\d+)-(\d+)/);
                    if (match) {
                        const profileBusinessEntityId = match[1];
                        const profileId = match[2];
                        data.manifest.components[0].profileBusinessEntityId = profileBusinessEntityId
                        data.manifest.components[0].profileId = profileId
                    }
                    const profileTask = {
                        "type": 5,
                        "instructions": {
                            "destination": 6
                        }
                    }
                    data.manifest.tasks.push(profileTask)
                }
                data.manifest.components[0].runInstall = true
            }
            else if (installationModeSignal.value === InstallationModeEnum.EXISTING_INSTALL) {
                data.type = InstallationModeEnum.EXISTING_INSTALL
                data.manifest.agent.downloadLocation = getAgentDownloadLocation()
                data.manifest.agent.agentVersion = extractVersion(agentVersionTargetSignal.value)
                const existingInstallTask = {
                    "type": 5,
                    "instructions": {
                        "destination": 10
                    }
                }
                data.manifest.tasks.push(existingInstallTask)
            }

            return data
        }
    )

    const getAgentDownloadLocation = () => {
        const targetAgent = availableAgentsVersions.value.find(item => item.version === agentVersionTargetSignal.value);
        if (targetAgent == null) {
            return null
        }
        return targetAgent.downloadLocation
    }

    const getUtgDownloadLocation = () => {
        const targetUtg = availableUtgVersions.value.find(item => item.version === utgVersionTargetSignal.value);
        if (targetUtg == null) {
            return null;
        }
        return targetUtg.downloadLocation
    }

    const extractVersion = (versionString) => {
        if (typeof versionString != 'string') {
            return null
        }
        if (/^\d+\.\d+\.\d+\.\d+$/.test(versionString)) {
            return versionString; // Return it directly if it's already a version
        }
        // Extract version number from "Track GA (Currently: X.X.X.X)"
        const match = versionString.match(/(\d+\.\d+\.\d+\.\d+)/);
        return match ? match[0] : null; // Return extracted version or null if not found
    }

    const validateFilePath = () => {
        formErrorTextSignal.value = ''
        const filePathRegex = /^[a-zA-Z]:[\\/](?:[\w\-\s.]+[\\/])*[\w\-\s.]+[\\/]?$/
        const isUtgLocationValid = filePathRegex.test(utgInstallLocationSignal.value)
        const isAgentLocationValid = filePathRegex.test(agentInstallLocationSignal.value)
        if (!isUtgLocationValid && !isAgentLocationValid) {
            formErrorTextSignal.value = `The UTG Install Location provided is not a valid path. The Agent Install Location provided is not a valid path.`
            formErrorSignal.value = true
        } else if (!isUtgLocationValid) {
            formErrorTextSignal.value = `The UTG Install Location provided is not a valid path.`
            formErrorSignal.value = true
        } else if (!isAgentLocationValid) {
            formErrorTextSignal.value = `The Agent Install Location provided is not a valid path.`
            formErrorSignal.value = true
        } else {
            formErrorSignal.value = false
        }
    }

    function AgentVersionSelect() {
        if (availableAgentsVersions.value) {
            let sorted = availableAgentsVersions.value.sort((a, b) => {
                const trackga = "Track GA (Currently: "
                const v1 = a.version
                if (v1.startsWith(trackga)) {
                    return -1
                }
                const v2 = b.version
                if (v2.startsWith(trackga)) {
                    return 1
                }
                else if (v1 < v2) {
                    return -1
                }
                return 1
            })
            let items = sorted.map(a => {
                return (
                    <option value={a.version} key={a.version}>{a.version}</option>
                )
            })

            return (
                <Form.Select onInput={(e) => agentVersionTargetSignal.value = e.target.value} value={agentVersionTargetSignal.value}>
                    {items}
                </Form.Select>
            )
        }
    }

    function ProfileSelect() {
        if (availableProfiles) {
            let items = availableProfiles.map(a => {
                return (
                    <option value={`${a.businessEntityId}-${a.id}`} key={a.id}>{a.name}</option>
                )
            })

            return (
                <Form.Select onInput={(e) => profileTargetSignal.value = e.target.value} value={profileTargetSignal.value}>
                    <option value="No Profile">No Profile</option> {/* "No Profile" option added here */}
                    {items}
                </Form.Select>
            )
        }
    }

    function UtgVersionSelect() {
        if (availableUtgVersions.value) {
            let sorted = availableUtgVersions.value.sort((a, b) => {
                const trackga = "Track GA (Currently "
                const v1 = a.version
                if (v1.startsWith(trackga)) {
                    return -1
                }
                const v2 = b.version
                if (v2.startsWith(trackga)) {
                    return 1
                }
                else if (v1 < v2) {
                    return -1
                }
                return 1
            })
            let items = sorted.map(a => {
                return (
                    <option value={a.version} key={a.version}>{a.version}</option>
                )
            })

            return (
                <Form.Select onInput={(e) => utgVersionTargetSignal.value = e.target.value} value={utgVersionTargetSignal.value}>
                    {items}
                </Form.Select>
            )
        }
    }

    return (
        <Modal
            show={show}
            onHide={handleCancel}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                {(installationModeSignal.value === InstallationModeEnum.INIT) && (
                    <Modal.Title>
                        What would you like to do?
                    </Modal.Title>
                )}
                {(installationModeSignal.value === InstallationModeEnum.NEW_INSTALL) && (
                    <Modal.Title>
                        Install and Connect a New UTG
                    </Modal.Title>
                )}
                {(installationModeSignal.value === InstallationModeEnum.EXISTING_INSTALL) && (
                    <Modal.Title>
                        Connect a UTG to Shift4 Command Center
                    </Modal.Title>
                )}

            </Modal.Header>
            <Modal.Body>
                {(installationModeSignal.value === InstallationModeEnum.INIT) && (
                    <Container className="d-flex flex-column align-items-center">
                        <Container className="mb-1 text-center">
                            <Button variant="outline-secondary" onClick={
                                () => {
                                    installationModeSignal.value = InstallationModeEnum.EXISTING_INSTALL;
                                    utgInstallLocationSignal.value = utgInstallLocationEnum.EXISTING_INSTALL;
                                }
                            }>
                                Connect an Existing UTG <Icon icon="plug" width={16} height={16}></Icon>
                            </Button>
                            <p className="mt-2">
                                If you are an existing Shift4 customer that is new to Shift4 Command Center, this is the correct option.
                            </p>
                        </Container>
                        <hr className="w-100" />
                        <Container className="mb-1 text-center">
                            <Button variant="outline-secondary" onClick={
                                () => {
                                    installationModeSignal.value = InstallationModeEnum.NEW_INSTALL;
                                    utgInstallLocationSignal.value = utgInstallLocationEnum.NEW_INSTALL;
                                }
                            }>
                                Install and Connect a New UTG <Icon icon="plus-circle" width={16} height={16}></Icon>
                            </Button>
                            <p className="mt-2">
                                If you are a new Shift4 customer, or if you are adding an additional UTG, this is the correct option.
                            </p>
                        </Container>
                    </Container>
                )}

                {(installationModeSignal.value === InstallationModeEnum.EXISTING_INSTALL ||
                    installationModeSignal.value === InstallationModeEnum.NEW_INSTALL) && (
                        <Form>
                            {(installationModeSignal.value === InstallationModeEnum.NEW_INSTALL) && (
                                <p>
                                    Complete the form below to install and connect a new Universal Transaction Gateway (UTG) to the Shift4 Command Center application. After the installation package is ran, the new UTG will automatically be connected to Shift4 Command Center. This will allow the UTG to be managed by the Shift4 Command Center web application, which includes executing tasks from the browser on the UTG and making configuration changes.
                                </p>
                            )}
                            {(installationModeSignal.value === InstallationModeEnum.EXISTING_INSTALL) && (
                                <p>
                                    Complete the form below to connect a Universal Transaction Gateway(UTG) to the Shift4 Command Center application. This will allow the UTG to be managed by the Shift4 Command Center web application, which includes executing useful tasks from the browser on the UTG and making configuration changes.
                                </p>
                            )}
                            <hr />

                            {/* Installation Name */}
                            <Form.Group className="mb-3" controlId="installationName">
                                <Form.Label className="d-block">Installation Name</Form.Label>
                                <Form.Control type="text" className="d-block" onChange={(event) => installationNameSignal.value = event.target.value} value={installationNameSignal.value} />
                                <Form.Text>Name that will be associated with the installation package.</Form.Text>
                            </Form.Group>

                            {/* Agent Version */}
                            <Form.Group className="mb-3" controlId="availableAgents">
                                <Form.Label>Pick Agent Version</Form.Label>
                                <AgentVersionSelect />
                                <Form.Text>The selected agent version will be installed when using this installation manifest.</Form.Text>
                            </Form.Group>

                            {/* Agent Install Location */}
                            <Form.Group className="mb-3" controlId="agentInstallLocation">
                                <Form.Label className="d-block">Agent Install Location</Form.Label>
                                <Form.Control type="text" className="d-block" onChange={(event) => { agentInstallLocationSignal.value = event.target.value; validateFilePath(); }} value={agentInstallLocationSignal.value} />
                                <Form.Text>Location where the Shift4 Command Center agent will be installed to.</Form.Text>
                            </Form.Group>

                            {/* UTG Version */}
                            {installationModeSignal.value === InstallationModeEnum.NEW_INSTALL && (
                                <Form.Group className="mb-3" controlId="availableUtgs">
                                    <Form.Label>Pick UTG Version</Form.Label>
                                    <UtgVersionSelect />
                                    <Form.Text>The selected UTG version will be installed when using this installation manifest.</Form.Text>
                                </Form.Group>
                            )}

                            {/* UTG Install Location */}
                            <Form.Group className="mb-3" controlId="utgInstallLocation">
                                <Form.Label className="d-block">UTG Install Location</Form.Label>
                                <Form.Control type="text" className="d-block" onChange={(event) => { utgInstallLocationSignal.value = event.target.value; validateFilePath(); }} value={utgInstallLocationSignal.value} />
                                {installationModeSignal.value === InstallationModeEnum.NEW_INSTALL && (
                                    <Form.Text>Location where the UTG will be installed to.</Form.Text>
                                )}
                                {installationModeSignal.value === InstallationModeEnum.EXISTING_INSTALL && (
                                    <Form.Text>Location on the machine that the utg2.exe is located.</Form.Text>
                                )}
                            </Form.Group>

                            {/* Profile */}
                            {installationModeSignal.value === InstallationModeEnum.NEW_INSTALL && (
                                <Form.Group className="mb-3" controlId="profile">
                                    <Form.Label>Profile</Form.Label>
                                    <ProfileSelect />
                                </Form.Group>
                            )}

                            {formErrorSignal.value && (
                                <Form.Group className="mb-3" controlId="error">
                                    <Form.Text className="fw-bold text-danger">
                                        {formErrorTextSignal.value}
                                    </Form.Text>
                                </Form.Group>
                            )}
                        </Form>
                    )}
            </Modal.Body>

            {(installationModeSignal.value === InstallationModeEnum.INIT) && (
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCancel}>Close</Button>
                </Modal.Footer>
            )}

            {(installationModeSignal.value === InstallationModeEnum.EXISTING_INSTALL ||
                installationModeSignal.value === InstallationModeEnum.NEW_INSTALL) && (
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => installationModeSignal.value = InstallationModeEnum.INIT}>Back</Button>
                        <Button
                            disabled={installationNameSignal.value === "" || formErrorSignal.value}
                            variant="primary"
                            onClick={
                                () => {
                                    installationManifestObject.value = installationObjectComputed.value
                                    handleConfirm()
                                }
                            }>
                            Create Install Package
                        </Button>
                    </Modal.Footer>
                )}
        </Modal>
    );
}

export default CreateInstallationModal;

